import { faMinus } from '@fortawesome/pro-light-svg-icons/faMinus';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import searchPresets from '@propertypal/shared/src/constants/searchPresets.json';
import alertIcon from '@propertypal/shared/src/resources/icons/tabs/alert.svg?url';
import chevRight from '@propertypal/shared/src/resources/icons/tabs/chevRight.svg?url';
import favouritesIcon from '@propertypal/shared/src/resources/icons/tabs/favourites.svg?url';
import homeIcon from '@propertypal/shared/src/resources/icons/tabs/home.svg?url';
import menuIcon from '@propertypal/shared/src/resources/icons/tabs/menu.svg?url';
import searchIcon from '@propertypal/shared/src/resources/icons/tabs/search.svg?url';
import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import { ROI_LINKS, STANDARD_LINKS } from '@propertypal/web-app/src/constants/navigation';
import togglePageScroll from '@propertypal/web-app/src/utils/togglePageScroll';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { Text } from '../typography';
import { Pill } from './Header.style';
import {
  Container,
  CustomButton,
  Item,
  LinkBox,
  Links,
  MoreMenu,
  OptionBox,
  CustomMenuButton,
  MenuIcon,
  SubmenuOptions,
  SubLinkBox,
  Icon,
} from './MobileFooter.style';

interface NavLink {
  text: string;
  url?: string;
  Component?: () => JSX.Element[];
  sublinks?: NavLink[];
  testID?: string;
  niOnly?: boolean;
  isNew?: boolean;
}

interface Props {
  cfIpCountry: string | null;
}

const NAV_LINKS = [
  {
    title: 'Home',
    shortTitle: 'Home',
    icon: homeIcon,
    testID: 'homeTab',
    url: '/',
    isActive: (url: string) => url === '/',
  },
  {
    title: 'Search',
    shortTitle: 'Search',
    icon: searchIcon,
    testID: 'searchTab',
    url: '/property-for-sale',
    isActive: (url: string) => {
      let active = false;

      searchPresets.urls.forEach((presetUrl) => {
        if (url.includes(presetUrl)) active = true;
      });

      return active;
    },
  },
  {
    title: 'Favourites',
    shortTitle: 'Favourites',
    icon: favouritesIcon,
    testID: 'savedTab',
    url: '/my/favourites',
    isActive: (url: string) => url === '/my/favourites',
  },
  {
    title: 'Saved Searches',
    shortTitle: 'Saved',
    icon: alertIcon,
    testID: 'alertsTab',
    url: '/my/alerts',
    isActive: (url: string) => url === '/my/alerts',
  },
];

const MobileFooter: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const router = useRouter();
  const [activeMore, setActiveMore] = useState<boolean>(false);
  const [activeSubLinks, setActiveSubLinks] = useState('');
  const moreLinks = props.cfIpCountry === 'IE' ? ROI_LINKS : STANDARD_LINKS;
  const toggleMoreMenu = () => {
    setActiveMore(!activeMore);
    togglePageScroll('#moreMenu', activeMore);
  };

  const toggleOption = (item: string) => {
    setActiveSubLinks(item === activeSubLinks ? '' : item);
  };

  useEffect(() => {
    const handleRouteChange = () => {
      if (activeMore) {
        // close menu on route.
        setActiveMore(false);
        togglePageScroll('#moreMenu', true);
      }
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [activeMore]);

  const generateOptions = (active: boolean, sublinks: NavLink[]) => {
    return (
      <SubmenuOptions role="menu" show={active}>
        {sublinks.map((sublink) => {
          return (
            sublink.url && (
              <SubLinkBox href={sublink.url} key={sublink.url} prefetch={false} role="menuitem">
                <Text fontSize={16} color={router?.asPath === sublink.url ? theme.primary : theme.textDark}>
                  {sublink.text}
                </Text>

                <img
                  src={chevRight.src}
                  width={chevRight.width}
                  height={chevRight.height}
                  alt="arrow right"
                  loading="lazy"
                />
              </SubLinkBox>
            )
          );
        })}
      </SubmenuOptions>
    );
  };

  const generateSubLinks = (sublinks: NavLink[]) => {
    const trackLink = (link: NavLink) => () => {
      if (link.url === '/instant-valuation') {
        trackGaEvent('valuation_click', {
          link_location: 'Mobile Navigation',
          type: 'instant',
        });
      } else if (link.url === '/agent-valuation') {
        trackGaEvent('valuation_click', {
          link_location: 'Mobile Navigation',
          type: 'agent',
        });
      }
    };

    return sublinks.map((link: NavLink) => {
      return (
        <li key={link.text}>
          {!!link.sublinks && (
            <>
              <CustomMenuButton onClick={() => toggleOption(link.text)} data-testid={link.text}>
                <OptionBox role="menuitem">
                  <Text color={router?.asPath === link.url ? theme.primary : theme.textDark} fontWeight="bold">
                    {link.text}{' '}
                    {link.isNew && (
                      <>
                        &nbsp;
                        <Pill data-testid={`${link.testID}New`}>New</Pill>
                      </>
                    )}
                  </Text>
                  <MenuIcon>
                    {link.text === activeSubLinks && (
                      <FontAwesomeIcon icon={faMinus} color={theme.textDark} size="2x" />
                    )}
                    {link.text !== activeSubLinks && <FontAwesomeIcon icon={faPlus} color={theme.textDark} size="2x" />}
                  </MenuIcon>
                </OptionBox>
              </CustomMenuButton>

              {generateOptions(link.text === activeSubLinks, link.sublinks)}
            </>
          )}

          {!!link.url && (
            <LinkBox
              href={link.url}
              prefetch={false}
              role="menuitem"
              data-testid={link.testID}
              onClick={trackLink(link)}
            >
              <Text color={router?.asPath === link.url ? theme.primary : theme.textDark} fontWeight="bold">
                {link.text}{' '}
                {link.isNew && (
                  <>
                    &nbsp;
                    <Pill data-testid={`${link.testID}New`}>New</Pill>
                  </>
                )}
              </Text>

              <img
                src={chevRight.src}
                width={chevRight.width}
                height={chevRight.height}
                alt="arrow right"
                loading="lazy"
              />
            </LinkBox>
          )}
        </li>
      );
    });
  };

  return (
    <Container className="hide-with-keyboard">
      <Links role="menu">
        {NAV_LINKS.map((item) => {
          const active = item.isActive(router.asPath) && !activeMore;

          return (
            <Item role="none" key={item.title}>
              <Link href={item.url} prefetch={false} tabIndex={0} role="menuitem" data-testid={item.testID}>
                {item.icon && <Icon {...item.icon} active={active} alt="tab icon" loading="lazy" />}

                <Text
                  color={active ? theme.primary : theme.darkGrey}
                  fontWeight={active ? 'bold' : 'normal'}
                  align="center"
                >
                  <span className="itemTitle">{item.title}</span>
                  <span className="itemShortTitle">{item.shortTitle}</span>
                </Text>
              </Link>
            </Item>
          );
        })}

        <Item role="none">
          <CustomButton role="menuitem" data-testid="moreTab" tabIndex={0} onClick={toggleMoreMenu}>
            <Icon {...menuIcon} alt="menu icon" active={activeMore} loading="lazy" />

            <Text
              color={activeMore ? theme.primary : theme.darkGrey}
              fontWeight={activeMore ? 'bold' : 'normal'}
              align="center"
            >
              Menu
            </Text>
          </CustomButton>

          <MoreMenu
            id="moreMenu"
            data-testid={`${props.cfIpCountry}-links`}
            style={{ display: activeMore ? 'block' : 'none' }}
          >
            {generateSubLinks(moreLinks)}
          </MoreMenu>
        </Item>
      </Links>
    </Container>
  );
};

export default MobileFooter;
